import type { Schema } from '@/shared/api/contract';

/**
 * Straight from the contract: cubas.domain.schemas.auth. `User` is `SessionRead`
 * (contract v10.9): `UserRead` plus `features`, returned by every endpoint that opens
 * or renews a session. `acceptInvite` does not open one, so it stays on `UserRead`.
 */
export type User = Schema<'SessionRead'>;
export type LoginPayload = Schema<'LoginRequest'>;
export type PasswordChange = Schema<'PasswordChange'>;

/**
 * What `/login` answers since contract v10.45 (ADR 0039 of the back): the session, as
 * before, or `SecondFactorPending` when the password was right but a code is still owed
 * (`second_factor`: `verify` or `setup`). That one carries no profile (no id, no name, no
 * mailboxes) and **opens no session**: the cookie only opens `/api/auth/two-factor/*` for
 * ten minutes and `/api/auth/me` still says 401. `isSecondFactorPending` tells them apart.
 */
export type SecondFactorPending = Schema<'SecondFactorPending'>;
export type LoginResult = User | SecondFactorPending;
export type SecondFactorStep = SecondFactorPending['second_factor'];
export type SecondFactorVerify = Schema<'SecondFactorVerify'>;
export type TwoFactorSetup = Schema<'TwoFactorSetupRead'>;
export type TwoFactorConfirm = Schema<'TwoFactorConfirm'>;
export type TwoFactorEnabled = Schema<'TwoFactorEnabledRead'>;
export type TwoFactorStatus = Schema<'TwoFactorStatusRead'>;
export type TrustedDevice = Schema<'TrustedDeviceRead'>;
export type RecoveryCodes = Schema<'RecoveryCodesRead'>;

/** The password was right and a code is still owed: nothing goes into the session cache.
 * A non-null check besides the key, so a back that sent `second_factor: null` with the
 * session still reads as a session. */
export function isSecondFactorPending(result: LoginResult): result is SecondFactorPending {
  return 'second_factor' in result && Boolean(result.second_factor);
}
